"use client";

import { useTranslations } from "@i18n/provider";
import { formatRelativeTime } from "@lib/formatRelativeTime";
import type { TutorSessionSummary } from "@lib/agent/tutorChat/types";

type Props = {
  open: boolean;
  sessions: TutorSessionSummary[];
  activeSessionId: number | null;
  onNew: () => void;
  onSwitch: (id: number) => void;
};

export function TutorSessionsPanel({ open, sessions, activeSessionId, onNew, onSwitch }: Props) {
  const { t } = useTranslations();
  if (!open) return null;

  return (
    <div
      id="tutor-sessions-panel"
      className="flex-shrink-0 px-3 py-2"
      style={{ borderBottom: "1px solid var(--hairline)", background: "var(--surface-sunk)" }}
    >
      <button
        type="button"
        onClick={onNew}
        className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-sm font-semibold"
        style={{ color: "var(--coral)" }}
      >
        <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
          forum
        </span>
        {t("agent.tutorSessions.new")}
      </button>

      {sessions.length === 0 ? (
        <p className="px-3 py-2 text-xs" style={{ color: "var(--ink-faint)" }}>
          {t("agent.tutorSessions.empty")}
        </p>
      ) : (
        <ul className="mt-1 flex flex-col gap-1">
          {sessions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSwitch(s.id)}
                aria-current={s.id === activeSessionId ? "true" : undefined}
                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl px-3 text-left transition-colors"
                style={{ background: s.id === activeSessionId ? "var(--surface)" : "transparent" }}
              >
                <span
                  className="min-w-0 flex-1 truncate text-sm"
                  style={{ color: "var(--ink-body)" }}
                >
                  {s.title || s.preview || t("agent.tutorSessions.untitled")}
                </span>
                <span className="flex-shrink-0 text-[10px]" style={{ color: "var(--ink-faint)" }}>
                  {formatRelativeTime(s.updatedAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
